import axiosInstance from 'config/axiosInstance';
import { getSessionGeneration, getSessionIdentity } from 'config/session';
import {
  CreateResource,
  discardCreateRecord,
  readCreateRecord,
  writeCreateRecord,
} from 'config/createRecordStore';
import {
  isInProgress,
  isOutcomeUnknown,
  newRequestId,
  OutcomeUnknownError,
  tellsOriginalOutcome,
} from 'components/Driver/registerDriver';

/**
 * 管理画面の作成（POST）を、結果が分からない失敗の後も二重に作らずに取り直せるようにする
 * 共通の仕組み。EV-128（配送先）で作り、EV-129（作業マスタ）・EV-130（機器）・EV-131（車両）
 * でも使う。取り決めは docs/api/idempotency.md。
 *
 * - 作成の操作ごとに X-Request-ID を作る。サーバーは同じ値・同じ本文の再送に前回の応答を
 *   返す（行は増えない）。
 * - 結果が分からない失敗（応答なし・timeout・5xx・409 idempotency_in_progress）では、同じ値・
 *   同じ本文で1回送り直す。処理中（409 idempotency_in_progress）の間は、同じ値で終わるまで
 *   （createTiming.maxInProgressPolls 回まで）問い合わせ直す。
 * - それでも分からなければ OutcomeUnknownError（submitIdempotentCreate では 'unknown'）。
 *   呼び出し側は「確かめられなかった」と示して入力を残し、一覧を読み直す。
 * - 結果が分からなかった作成の値と本文はタブに残し（RV-130）、入力を変えずにもう一度押したら
 *   同じ値で送る（前回が確定していればリプレイが返る）。
 */

/** 送り直すまでの待ち時間と、処理中を問い合わせ直す上限の回数（テストで短くできるように）。 */
export const createTiming = { retryDelayMs: 1000, maxInProgressPolls: 20 };

export { OutcomeUnknownError };

/**
 * RV-127。サーバーが X-Request-ID の記録（前回の応答）を残す期間。サーバーの
 * IDEMPOTENCY_TTL（services/backend/api/idempotency.py の _ttl、既定 24 時間。
 * docs/api/idempotency.md）と必ず一致させる。期限を過ぎた値の再送は新しい作成として
 * 扱われ、前回が確定していれば二重に作る。
 */
export const IDEMPOTENCY_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * 結果が分からなかった作成の X-Request-ID を使い続けてよい長さ。時計のずれ・送信と
 * 処理の時間を見込み、期限の1時間前（23時間）で打ち切る。
 */
export const REQUEST_ID_REUSE_MS = IDEMPOTENCY_TTL_MS - 60 * 60 * 1000;

/**
 * EV-139。本文を、項目の順番に左右されない文字列にする（キーを並べ替える）。同じ内容なら
 * 同じ文字列になり、同じ X-Request-ID を使う。配列の順番はそのまま（意味がある場合がある。
 * 順番に意味が無い配列は、呼び出し側で並べてから渡す。配送先の運転者など）。
 */
export function canonicalBody(value: unknown): string {
  const sort = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(sort);
    if (v && typeof v === 'object') {
      return Object.keys(v as object)
        .sort()
        .reduce((acc, key) => {
          acc[key] = sort((v as Record<string, unknown>)[key]);
          return acc;
        }, {} as Record<string, unknown>);
    }
    return v;
  };
  return JSON.stringify(sort(value));
}

/**
 * RV-130。結果が分からない（または送信の途中の）作成の X-Request-ID と本文。
 *
 * 画面を離れても（リンク・戻る・再読み込み・ダイアログを閉じる）失わないよう、このタブの
 * sessionStorage に残す（config/createRecordStore。作るものごとに別のキー）。送る前に書き、
 * 成功したとき・前回の結果が確かめられたとき（establishesOutcome）に消す。結果が分からない
 * ままなら残る。
 *
 * 利用者の印（getSessionIdentity。「sid:user_id」、ログインごとに変わる）を付け、今の印と
 * 違えば使わずに消す。別の利用者・ログインし直した後の作成に、前の値を使わない。
 * 更新の世代（getSessionGeneration）は access の更新のたびに変わるので付けない。
 *
 * sessionStorage が使えない環境では、このタブのメモリに残す（RV-132。再読み込みでは失う）。
 */
export interface UnconfirmedCreate {
  requestId: string;
  body: string;
  /** その値で最初に送った時刻（RV-127）。 */
  savedAt: number;
  /** 期限が近く、この値を使えないと警告した後か（RV-127）。 */
  expiredWarned: boolean;
  /**
   * EV-140。その値で送ったときのセッションの印（getSessionIdentity）。省くと、書くときの
   * 印を使う。結果不明の書き直しは、送ったときの印で書く（送信の途中で別の利用者に
   * 替わっても、前の利用者の値を今の利用者の値にしない。load が捨てる）。
   */
  session?: string | null;
}

export interface UnconfirmedStore {
  load(): UnconfirmedCreate | null;
  /**
   * 値を残す。EV-138。onlyOwn: true のときは、残っている値が無いか、同じ作成（requestId と
   * 本文の両方が同じ。RV-131）のときだけ書く。新しい作成を送る前（利用者の新しい操作）だけ、
   * onlyOwn なしで上書きする。
   */
  save(value: UnconfirmedCreate, options?: { onlyOwn?: boolean }): void;
  /**
   * 値を消す。EV-138・RV-131。requestId を渡したときは、残っている値がその作成（requestId と
   * 本文の両方が一致）のときだけ消す（比べて消す）。引数なしは無条件に消す。
   */
  clear(requestId?: string, body?: string): void;
}

/** 作るもの（resource）ごとの、結果が分からない作成の値の置き場。 */
export function unconfirmedStore(resource: CreateResource): UnconfirmedStore {
  /** 残っている値の requestId と本文（利用者の印は見ない）。無ければ null。 */
  const storedKey = (): { requestId: string; body: string } | null => {
    const stored = readCreateRecord(resource);
    return typeof stored?.requestId === 'string' && typeof stored?.body === 'string'
      ? { requestId: stored.requestId, body: stored.body }
      : null;
  };
  /** RV-131。残っている値が、この作成（requestId と本文の両方）のものか。 */
  const isOwn = (
    key: { requestId: string; body: string } | null,
    requestId: string,
    body: string
  ) => key !== null && key.requestId === requestId && key.body === body;

  const clear = (requestId?: string, body?: string): void => {
    if (requestId !== undefined && !isOwn(storedKey(), requestId, body ?? '')) return;
    discardCreateRecord(resource);
  };

  const load = (): UnconfirmedCreate | null => {
    // RV-132。残っている値が無ければメモリの値（書けない環境でも失わない）。
    const stored = readCreateRecord(resource);
    if (
      !stored ||
      typeof stored.requestId !== 'string' ||
      typeof stored.body !== 'string' ||
      typeof stored.savedAt !== 'number' ||
      stored.session !== getSessionIdentity()
    ) {
      if (stored) clear();
      return null;
    }
    return {
      requestId: stored.requestId,
      body: stored.body,
      savedAt: stored.savedAt,
      expiredWarned: stored.expiredWarned === true,
      session: stored.session,
    };
  };

  const save = (
    value: UnconfirmedCreate,
    { onlyOwn = false }: { onlyOwn?: boolean } = {}
  ): void => {
    if (onlyOwn) {
      const current = storedKey();
      if (current !== null && !isOwn(current, value.requestId, value.body)) return;
    }
    writeCreateRecord(resource, {
      ...value,
      session: value.session !== undefined ? value.session : getSessionIdentity(),
    });
  };

  return { load, save, clear };
}

/**
 * RV-128。結果が分からなかった作成と同じ X-Request-ID・同じ本文で送り直して失敗したとき、
 * その失敗が元の要求の結果を確かめたか（保存した値を捨ててよいか）。
 *
 * サーバーは認証・権限・回数制限（401・403・429）を X-Request-ID の処理（リプレイ）より前に
 * 行う。それらで断られた要求は前回の記録を見ていないので、前回が確定していたかは分からない。
 * 確かめたとみなすのは、X-Request-ID の処理を通った応答だけ:
 * - 400: 同じ値・同じ本文で、前回が確定していればリプレイ（2xx）が返るはず。項目のエラーが
 *   返ったなら、前回は確定していない。
 * - 409 idempotency_conflict: この値はもう使えない。捨てて新しい値にする。
 * それ以外（401・403・404・408・429・5xx・応答なし・別の利用者への切り替えなど）は値を残す。
 */
export function establishesOutcome(error: any): boolean {
  const response = error?.response;
  if (!response) return false;
  if (response.status === 400) return true;
  return response.status === 409 && response.data?.code === 'idempotency_conflict';
}

const wait = (ms: number) =>
  ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();

/**
 * X-Request-ID を付けて POST し、結果が分からない失敗では同じ値・同じ本文で送り直す。
 * それでも分からなければ OutcomeUnknownError を投げる。
 */
export async function postIdempotent(
  url: string,
  payload: { [key: string]: any },
  requestId: string
): Promise<any> {
  // 冪等の範囲は利用者ごと。作成を始めたときのセッションを控え、1回目と送り直しの両方に
  // その印（_sentWith）を付ける（registerDriver と同じ。PR #104 レビュー）。印が今の
  // セッションと違えば、axiosInstance の interceptor が送らずに止める。
  const held = { session: getSessionIdentity(), generation: getSessionGeneration() };
  const send = () =>
    axiosInstance.post(url, payload, {
      headers: { 'X-Request-ID': requestId },
      _sentWith: held,
    } as any);

  let last: unknown;
  try {
    return await send();
  } catch (first) {
    if (!isOutcomeUnknown(first)) throw first;
    last = first;
  }

  // 結果が分からない失敗（応答なし・5xx）の送り直しは1回まで。処理中（409）の問い合わせ
  // 直しは別に数え、上限の回数まで続ける。
  let resends = 0;
  let polls = 0;
  for (;;) {
    if (isInProgress(last)) {
      if (polls >= createTiming.maxInProgressPolls) throw new OutcomeUnknownError(last);
      polls += 1;
    } else {
      if (resends >= 1) throw new OutcomeUnknownError(last);
      resends += 1;
    }
    await wait(createTiming.retryDelayMs);
    if (getSessionIdentity() !== held.session) {
      // 送り直す前にセッションが変わっていた。1回目の結果は分からないまま。
      throw new OutcomeUnknownError(new Error('The session changed before resending'));
    }
    try {
      return await send();
    } catch (error) {
      // 送り直しで元の要求の結果が分かった失敗（項目のエラー・idempotency_conflict）だけ返す。
      if (tellsOriginalOutcome(error)) throw error;
      // 冪等の処理に届く前に断られた（401・別の利用者への切り替えなど）なら、分からないまま。
      if (!isOutcomeUnknown(error)) throw new OutcomeUnknownError(error);
      last = error;
    }
  }
}

/**
 * submitIdempotentCreate の結果。
 * - created: 作成できた（リプレイを含む）。残した値は消した。
 * - expired: RV-127。前回の値は期限が近く使えない。何も送っていない。呼び出し側は一覧を
 *   読み直して「作成済みでないか確かめる」よう警告する。次の押下は新しい値で送る。
 * - unknown: 送り直しても結果が分からない。値は残した。呼び出し側は入力を送った内容のまま
 *   残し、一覧を読み直して「確かめられなかった」と示す。
 * - failed: 結果が分かる失敗（400・403 など）。keptRecord が true なら、前回の値で送り直して
 *   X-Request-ID の処理の前に断られた（RV-128）ので値を残した（入力を送った内容に戻す）。
 */
export type CreateOutcome =
  | { status: 'created'; response: any }
  | { status: 'expired' }
  | { status: 'unknown'; error: OutcomeUnknownError }
  | { status: 'failed'; error: unknown; keptRecord: boolean };

/**
 * 作成を1回の操作として送る（値の作り直し・使い回し・残す・消すまで）。
 *
 * payload は送る本文そのもの。入力を変えずにもう一度押したときに同じ値を使うかは、
 * canonicalBody(payload) が前回と同じかで決める。
 * scope を渡したときは、scope と本文の両方が前回と同じかで決める。
 */
export async function submitIdempotentCreate(
  resource: CreateResource,
  url: string,
  payload: { [key: string]: any },
  scope?: string
): Promise<CreateOutcome> {
  const store = unconfirmedStore(resource);
  // YAC-2026-009 PR5b。scope は、同じ本文でも別の宛先（会社ごとの管理者の招待など）の作成を
  // 別の操作として扱うための印。渡したときだけ本文の比べ方に含める（省けば今までと同じ）。
  const body = scope === undefined ? canonicalBody(payload) : canonicalBody({ scope, payload });
  const stored = store.load();
  const previous = stored?.body === body ? stored : null;
  // RV-127。余裕（REQUEST_ID_REUSE_MS）を過ぎたら、古い値で黙って送らない。警告の後に
  // もう一度押したときだけ、新しい値で作る。
  if (
    previous &&
    !previous.expiredWarned &&
    Date.now() - previous.savedAt >= REQUEST_ID_REUSE_MS
  ) {
    store.save({ ...previous, expiredWarned: true }, { onlyOwn: true });
    return { status: 'expired' };
  }
  const reuse = previous && !previous.expiredWarned ? previous : null;
  const requestId = reuse ? reuse.requestId : newRequestId();
  // RV-130。送る前に残す。送信の途中で画面を離れても、戻って同じ内容で作成すれば同じ値で
  // 送る。新しい操作なので、ほかの値があっても上書きする。EV-140。送るときのセッションの
  // 印も残す（postIdempotent が送信に付ける印と同じ時点）。
  const record: UnconfirmedCreate = {
    requestId,
    body,
    savedAt: reuse ? reuse.savedAt : Date.now(),
    expiredWarned: false,
    session: getSessionIdentity(),
  };
  store.save(record);
  try {
    const response = await postIdempotent(url, payload, requestId);
    // EV-138。消すのは自分の値だけ。
    store.clear(requestId, body);
    return { status: 'created', response };
  } catch (error) {
    if (error instanceof OutcomeUnknownError) {
      // 値を残す。送る前に残した値が途中で消えていても必ず残るよう、もう一度書く。ただし、
      // ほかの画面の新しい作成の値は上書きしない（onlyOwn）。
      store.save(record, { onlyOwn: true });
      return { status: 'unknown', error };
    }
    // RV-128。前回の値で送り直して、X-Request-ID の処理より前に断られたなら、前回の結果は
    // まだ分からない。値と savedAt を残す。前回の結果が確かめられたときか、新しい値の失敗
    // なら捨てる。
    if (!reuse || establishesOutcome(error)) {
      store.clear(requestId, body);
      return { status: 'failed', error, keptRecord: false };
    }
    store.save(record, { onlyOwn: true });
    return { status: 'failed', error, keptRecord: true };
  }
}

/**
 * #387 の Codex の指摘 4368590863（RV-703）・EV-461。作成の結果が分からないまま（前回の X-Request-ID を残した）か。
 * このときは入力を送った値のまま固定し、「同じ内容で送り直す」か、やめるかだけにする（入力を変えると別の
 * X-Request-ID になり、確定していた作成を取り戻せず、二重に作ったり取り違えたりするため）。
 * expired（期限が近く、前回の値を使えない）は、警告の後の押し直しで新しい値にする作りなので固定しない。
 * 運営の画面（components/Operator）と既存の登録の画面（配送先・作業・機器・車両）で共通。
 */
export const isUnresolvedCreate = (outcome: CreateOutcome): boolean =>
  outcome.status === 'unknown' || (outcome.status === 'failed' && outcome.keptRecord);
